// packages/shared/src/constants/error-messages.ts
import { ErrorCode, type ErrorCodeValue } from './error-codes';

/**
 * User-friendly error message structure
 */
export interface UserFriendlyError {
  title: string;
  message: string;
  action?: string;
  actionKey?: 'retry' | 'login' | 'go_back' | 'contact_support' | 'choose_another';
}

/**
 * HTTP status code to error message mapping
 */
export const HTTP_STATUS_MESSAGES: Record<number, UserFriendlyError> = {
  400: {
    title: 'Bad Request',
    message: 'The request could not be processed. Please check your input.',
    action: 'Try again',
    actionKey: 'retry',
  },
  401: {
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again.',
    action: 'Go to Login',
    actionKey: 'login',
  },
  403: {
    title: 'Access Denied',
    message: "You don't have permission to perform this action.",
    action: 'Go Back',
    actionKey: 'go_back',
  },
  404: {
    title: 'Not Found',
    message: 'The requested resource could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  409: {
    title: 'Conflict',
    message: 'This operation conflicts with existing data.',
    action: 'Try again',
    actionKey: 'retry',
  },
  422: {
    title: 'Validation Error',
    message: 'Please check your input and try again.',
    action: 'Review form',
    actionKey: 'retry',
  },
  429: {
    title: 'Too Many Requests',
    message: 'Please wait a moment before trying again.',
    action: 'Wait and retry',
    actionKey: 'retry',
  },
  500: {
    title: 'Server Error',
    message: 'Something went wrong. Our team has been notified.',
    action: 'Try again later',
    actionKey: 'retry',
  },
  502: {
    title: 'Bad Gateway',
    message: 'Our servers are temporarily unavailable.',
    action: 'Retry',
    actionKey: 'retry',
  },
  503: {
    title: 'Service Unavailable',
    message: 'The service is temporarily unavailable.',
    action: 'Retry',
    actionKey: 'retry',
  },
};

/**
 * Error code to user-friendly message mapping
 */
export const ERROR_MESSAGES: Record<ErrorCodeValue, UserFriendlyError> = {
  // Network errors
  [ErrorCode.NETWORK_ERROR]: {
    title: 'Connection Error',
    message: 'Unable to connect. Please check your internet connection.',
    action: 'Try again',
    actionKey: 'retry',
  },
  [ErrorCode.TIMEOUT_ERROR]: {
    title: 'Request Timeout',
    message: 'The request took too long. Please try again.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.UNKNOWN_ERROR]: {
    title: 'Unexpected Error',
    message: 'An unexpected error occurred. Please try again.',
    action: 'Try again',
    actionKey: 'retry',
  },

  // Auth errors
  [ErrorCode.AUTH_INVALID_CREDENTIALS]: {
    title: 'Invalid Credentials',
    message: 'The email or password you entered is incorrect.',
    action: 'Try again',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_SESSION_EXPIRED]: {
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again.',
    action: 'Go to Login',
    actionKey: 'login',
  },
  [ErrorCode.AUTH_EMAIL_NOT_VERIFIED]: {
    title: 'Email Not Verified',
    message: 'Please verify your email address before logging in.',
    action: 'Resend verification',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_EMAIL_ALREADY_VERIFIED]: {
    title: 'Already Verified',
    message: 'Your email is already verified.',
    action: 'Go to login',
    actionKey: 'login',
  },
  [ErrorCode.AUTH_ACCOUNT_LOCKED]: {
    title: 'Account Locked',
    message: 'Your account has been locked. Please reset your password.',
    action: 'Reset password',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_ACCOUNT_DISABLED]: {
    title: 'Account Disabled',
    message: 'Your account has been disabled. Please contact support.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.AUTH_UNAUTHORIZED]: {
    title: 'Unauthorized',
    message: 'Please sign in to continue.',
    action: 'Sign in',
    actionKey: 'login',
  },
  [ErrorCode.AUTH_FORBIDDEN]: {
    title: 'Access Denied',
    message: "You don't have permission to access this resource.",
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.AUTH_INVALID_TOKEN]: {
    title: 'Invalid Token',
    message: 'The token is invalid or has expired.',
    action: 'Try again',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_CSRF_INVALID]: {
    title: 'Security Error',
    message: 'Please refresh the page and try again.',
    action: 'Refresh',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_USER_INACTIVE]: {
    title: 'Account Inactive',
    message: 'Your account is inactive. Please contact support.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.AUTH_INVALID_OAUTH_TOKEN]: {
    title: 'Authentication Failed',
    message: 'Unable to verify your identity with Google. Please try again.',
    action: 'Try again',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_OAUTH_EMAIL_MISSING]: {
    title: 'Email Required',
    message: 'We could not retrieve your email from Google. Please use a different sign-in method.',
    action: 'Try another method',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_INVALID_REFRESH_TOKEN]: {
    title: 'Session Invalid',
    message: 'Your session is invalid. Please sign in again.',
    action: 'Sign in',
    actionKey: 'login',
  },
  [ErrorCode.AUTH_REFRESH_TOKEN_EXPIRED]: {
    title: 'Session Expired',
    message: 'Your session has expired. Please sign in again.',
    action: 'Sign in',
    actionKey: 'login',
  },
  [ErrorCode.AUTH_REFRESH_TOKEN_REUSED]: {
    title: 'Security Alert',
    message: 'A security issue was detected. Please sign in again.',
    action: 'Sign in',
    actionKey: 'login',
  },
  [ErrorCode.AUTH_INVALID_VERIFICATION_TOKEN]: {
    title: 'Invalid Link',
    message: 'This verification link is invalid. Please request a new one.',
    action: 'Request new link',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_VERIFICATION_TOKEN_EXPIRED]: {
    title: 'Link Expired',
    message: 'This verification link has expired. Please request a new one.',
    action: 'Request new link',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_INVALID_RESET_TOKEN]: {
    title: 'Invalid Link',
    message: 'This password reset link is invalid. Please request a new one.',
    action: 'Request new link',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_RESET_TOKEN_EXPIRED]: {
    title: 'Link Expired',
    message: 'This password reset link has expired. Please request a new one.',
    action: 'Request new link',
    actionKey: 'retry',
  },
  [ErrorCode.AUTH_SESSION_NOT_FOUND]: {
    title: 'Session Not Found',
    message: 'The session could not be found.',
    action: 'Sign in',
    actionKey: 'login',
  },
  [ErrorCode.AUTH_PASSWORD_SAME_AS_CURRENT]: {
    title: 'Password Unchanged',
    message: 'Your new password must be different from your current password.',
    action: 'Try again',
    actionKey: 'retry',
  },

  // User errors
  [ErrorCode.USER_NOT_FOUND]: {
    title: 'User Not Found',
    message: 'The user could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.USER_EMAIL_EXISTS]: {
    title: 'Email Already Exists',
    message: 'An account with this email already exists.',
    action: 'Try login',
    actionKey: 'login',
  },
  [ErrorCode.USER_PHONE_EXISTS]: {
    title: 'Phone Already Exists',
    message: 'An account with this phone number already exists.',
    action: 'Try login',
    actionKey: 'login',
  },
  [ErrorCode.USER_INVALID_PASSWORD]: {
    title: 'Invalid Password',
    message: 'The current password is incorrect.',
    action: 'Try again',
    actionKey: 'retry',
  },

  // Venue errors
  [ErrorCode.VENUE_NOT_FOUND]: {
    title: 'Venue Not Found',
    message: 'This venue could not be found.',
    action: 'Browse venues',
    actionKey: 'go_back',
  },
  [ErrorCode.VENUE_NOT_APPROVED]: {
    title: 'Venue Not Available',
    message: 'This venue is not currently available for booking.',
    action: 'Browse venues',
    actionKey: 'go_back',
  },
  [ErrorCode.VENUE_CLOSED]: {
    title: 'Venue Closed',
    message: 'This venue is currently closed.',
    action: 'Choose another time',
    actionKey: 'choose_another',
  },
  [ErrorCode.VENUE_NO_AVAILABILITY]: {
    title: 'No Availability',
    message: 'No slots available for the selected date.',
    action: 'Choose another date',
    actionKey: 'choose_another',
  },
  [ErrorCode.VENUE_INVALID_TYPE]: {
    title: 'Invalid Venue Type',
    message: 'The venue type is not valid for this operation.',
    action: 'Browse venues',
    actionKey: 'go_back',
  },
  [ErrorCode.INVALID_VENUE_TYPE]: {
    title: 'Invalid Venue Type',
    message: 'The selected venue type is not valid.',
    action: 'Go Back',
    actionKey: 'go_back',
  },

  // Booking errors
  [ErrorCode.BOOKING_NOT_FOUND]: {
    title: 'Booking Not Found',
    message: 'This booking could not be found.',
    action: 'View all bookings',
    actionKey: 'go_back',
  },
  [ErrorCode.BOOKING_SLOT_UNAVAILABLE]: {
    title: 'Slot Unavailable',
    message: 'This time slot is no longer available.',
    action: 'Choose another slot',
    actionKey: 'choose_another',
  },
  [ErrorCode.BOOKING_ALREADY_CANCELLED]: {
    title: 'Already Cancelled',
    message: 'This booking has already been cancelled.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.BOOKING_CANCELLATION_NOT_ALLOWED]: {
    title: 'Cancellation Not Allowed',
    message: 'This booking cannot be cancelled.',
    action: 'View policy',
    actionKey: 'go_back',
  },
  [ErrorCode.BOOKING_PAYMENT_PENDING]: {
    title: 'Payment Pending',
    message: 'Please complete the payment to confirm your booking.',
    action: 'Complete payment',
    actionKey: 'retry',
  },
  [ErrorCode.BOOKING_ALREADY_COMPLETED]: {
    title: 'Booking Completed',
    message: 'This booking has already been completed.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.BOOKING_CREATION_FAILED]: {
    title: 'Booking Failed',
    message: 'Unable to create booking due to high demand. Please try again.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.BOOKING_LOCKED]: {
    title: 'Booking Locked',
    message: 'This booking cannot be modified after payout generation.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.BOOKING_NOT_CHECKED_IN]: {
    title: 'Not Checked In',
    message: 'This booking has not been checked in yet.',
    action: 'Check in first',
    actionKey: 'retry',
  },

  // Break Management errors
  [ErrorCode.BREAK_TRACKING_NOT_ENABLED]: {
    title: 'Break Tracking Disabled',
    message: 'Break tracking is not enabled for this venue.',
    action: 'Contact venue',
    actionKey: 'contact_support',
  },
  [ErrorCode.BREAK_ALREADY_ACTIVE]: {
    title: 'Break Already Active',
    message: 'A break is already in progress for this booking.',
    action: 'End current break',
    actionKey: 'retry',
  },
  [ErrorCode.BREAK_NOT_FOUND]: {
    title: 'Break Not Found',
    message: 'The requested break could not be found.',
    action: 'Go back',
    actionKey: 'go_back',
  },

  // Session Extension errors
  [ErrorCode.EXTENSION_SLOT_UNAVAILABLE]: {
    title: 'Slot Unavailable',
    message: 'The requested time slot is not available for extension.',
    action: 'Try a shorter duration',
    actionKey: 'retry',
  },
  [ErrorCode.EXTENSION_SESSION_NOT_ACTIVE]: {
    title: 'Session Not Active',
    message: 'Only active (checked-in) sessions can be extended.',
    action: 'Go back',
    actionKey: 'go_back',
  },
  [ErrorCode.EXTENSION_BREAK_ACTIVE]: {
    title: 'Break In Progress',
    message: 'Cannot extend a session while a break is active. End the break first.',
    action: 'End break',
    actionKey: 'retry',
  },
  [ErrorCode.EXTENSION_MAX_DURATION_EXCEEDED]: {
    title: 'Maximum Duration Exceeded',
    message: 'The extension would exceed the maximum allowed session duration.',
    action: 'Try a shorter duration',
    actionKey: 'retry',
  },

  // Payment errors
  [ErrorCode.PAYMENT_FAILED]: {
    title: 'Payment Failed',
    message: 'Your payment could not be processed.',
    action: 'Retry payment',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_TIMEOUT]: {
    title: 'Payment Timeout',
    message: 'The payment request timed out.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_CANCELLED]: {
    title: 'Payment Cancelled',
    message: 'The payment was cancelled.',
    action: 'Try again',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_INVALID_AMOUNT]: {
    title: 'Invalid Amount',
    message: 'The payment amount is invalid.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.PAYMENT_REFUND_FAILED]: {
    title: 'Refund Failed',
    message: 'The refund could not be processed.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.PAYMENT_NOT_FOUND]: {
    title: 'Payment Not Found',
    message: 'The payment record could not be found.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.PAYMENT_AMOUNT_MISMATCH]: {
    title: 'Payment Verification Failed',
    message: 'Payment verification failed. Please contact support if amount was deducted.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.PAYMENT_INVALID_MERCHANT]: {
    title: 'Invalid Merchant',
    message: 'The payment merchant configuration is invalid.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.PAYMENT_QR_GENERATION_FAILED]: {
    title: 'QR Code Generation Failed',
    message: 'Unable to generate payment QR code.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_COLLECT_PAY_FAILED]: {
    title: 'Payment Collection Failed',
    message: 'Unable to initiate payment collection.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_STATUS_QUERY_FAILED]: {
    title: 'Payment Status Check Failed',
    message: 'Unable to verify payment status.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_CALLBACK_DECRYPTION_FAILED]: {
    title: 'Payment Verification Failed',
    message: 'Unable to verify payment response.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.PAYMENT_CALLBACK_PROCESSING_FAILED]: {
    title: 'Payment Processing Failed',
    message: 'Unable to process payment confirmation.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.PAYMENT_UPI_ID_REQUIRED]: {
    title: 'UPI ID Required',
    message: 'Please enter your UPI ID to proceed with payment.',
    action: 'Try again',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_TRANSACTION_REF_MISSING]: {
    title: 'Transaction Reference Missing',
    message: 'Transaction reference is missing. Please try again.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_TRANSACTION_REF_INVALID]: {
    title: 'Invalid Transaction Reference',
    message: 'Invalid transaction reference. Please try again.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_VERIFICATION_FAILED]: {
    title: 'Payment Verification Failed',
    message: 'Payment verification failed. Please contact support if amount was deducted.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.PAYMENT_RETRY_LIMIT_EXCEEDED]: {
    title: 'Payment Retry Limit Exceeded',
    message: 'Maximum retry attempts reached. Please try making a new payment.',
    action: 'Try Again',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_RETRY_IN_PROGRESS]: {
    title: 'Payment Retry In Progress',
    message: 'Your payment is being retried. Please wait a moment.',
    action: 'Please Wait',
    actionKey: 'retry',
  },
  [ErrorCode.PAYMENT_RETRY_FAILED]: {
    title: 'Payment Retry Failed',
    message: 'Unable to retry payment after multiple attempts. Please contact support.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },

  // Subscription errors
  [ErrorCode.SUBSCRIPTION_NOT_FOUND]: {
    title: 'Subscription Not Found',
    message: 'Your subscription could not be found.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.SUBSCRIPTION_PLAN_NOT_FOUND]: {
    title: 'Plan Not Found',
    message: 'The selected subscription plan could not be found.',
    action: 'Choose Another',
    actionKey: 'choose_another',
  },

  // Mandate errors
  [ErrorCode.MANDATE_NOT_FOUND]: {
    title: 'Payment Method Not Found',
    message: 'Your payment method could not be found. Please set up payment again.',
    action: 'Set Up Payment',
    actionKey: 'retry',
  },
  [ErrorCode.MANDATE_REGISTRATION_FAILED]: {
    title: 'Payment Setup Failed',
    message: 'We could not set up your payment method. Please try again.',
    action: 'Try Again',
    actionKey: 'retry',
  },
  [ErrorCode.MANDATE_NOT_ACTIVE]: {
    title: 'Payment Method Inactive',
    message: 'Your payment method is not active. Please update your payment method.',
    action: 'Update Payment',
    actionKey: 'retry',
  },
  [ErrorCode.CARD_SI_LIMIT_EXCEEDED]: {
    title: 'Card Limit Exceeded',
    message: 'Card payment limit is Rs 15,000. Please use UPI AutoPay for higher amounts.',
    action: 'Use UPI',
    actionKey: 'retry',
  },

  // Wallet errors
  [ErrorCode.WALLET_NOT_FOUND]: {
    title: 'Wallet Not Found',
    message: 'Your wallet could not be found. Please contact support.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.WALLET_INSUFFICIENT_BALANCE]: {
    title: 'Insufficient Wallet Balance',
    message: 'You do not have enough credits in your wallet for this booking.',
    action: 'Adjust payment',
    actionKey: 'retry',
  },
  [ErrorCode.WALLET_NEGATIVE_BALANCE]: {
    title: 'Wallet Error',
    message: 'A wallet balance error was detected. Please contact support.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },
  [ErrorCode.WALLET_TRANSACTION_FAILED]: {
    title: 'Transaction Failed',
    message: 'The wallet transaction could not be completed. Please try again.',
    action: 'Try again',
    actionKey: 'retry',
  },
  [ErrorCode.WALLET_CREDIT_EXPIRED]: {
    title: 'Credits Expired',
    message: 'Some of your wallet credits have expired.',
    action: 'Continue',
    actionKey: 'retry',
  },
  [ErrorCode.WALLET_CREATION_FAILED]: {
    title: 'Wallet Creation Failed',
    message: 'Unable to process wallet credits. Please try again.',
    action: 'Retry',
    actionKey: 'retry',
  },

  // Vendor errors
  [ErrorCode.VENDOR_NOT_FOUND]: {
    title: 'Vendor Not Found',
    message: 'The vendor could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.VENDOR_NOT_APPROVED]: {
    title: 'Vendor Not Approved',
    message: 'This vendor is pending approval.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.VENDOR_ALREADY_EXISTS]: {
    title: 'Vendor Exists',
    message: 'A vendor profile already exists for this account.',
    action: 'Go to dashboard',
    actionKey: 'go_back',
  },

  // Coupon errors
  [ErrorCode.COUPON_NOT_FOUND]: {
    title: 'Coupon Not Found',
    message: 'This coupon code does not exist.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.COUPON_INVALID]: {
    title: 'Invalid Coupon',
    message: 'This coupon code is not valid.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.COUPON_EXPIRED]: {
    title: 'Coupon Expired',
    message: 'This coupon has expired.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.COUPON_USAGE_LIMIT_REACHED]: {
    title: 'Coupon Limit Reached',
    message: 'This coupon has reached its usage limit.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.COUPON_NOT_APPLICABLE]: {
    title: 'Coupon Not Applicable',
    message: 'This coupon cannot be applied to your order.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.COUPON_MINIMUM_NOT_MET]: {
    title: 'Minimum Not Met',
    message: 'Your order does not meet the minimum amount for this coupon.',
    action: 'Add more items',
    actionKey: 'go_back',
  },

  // Review errors
  [ErrorCode.REVIEW_NOT_FOUND]: {
    title: 'Review Not Found',
    message: 'This review could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.REVIEW_ALREADY_EXISTS]: {
    title: 'Review Exists',
    message: 'You have already reviewed this booking.',
    action: 'View review',
    actionKey: 'go_back',
  },
  [ErrorCode.REVIEW_NOT_ALLOWED]: {
    title: 'Review Not Allowed',
    message: 'You can only review completed bookings.',
    action: 'Go Back',
    actionKey: 'go_back',
  },

  // Favorite errors
  [ErrorCode.FAVORITE_NOT_FOUND]: {
    title: 'Not in Favorites',
    message: 'This venue is not in your favorites.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.FAVORITE_ALREADY_EXISTS]: {
    title: 'Already Favorited',
    message: 'This venue is already in your favorites.',
    action: 'View favorites',
    actionKey: 'go_back',
  },

  // Staff errors
  [ErrorCode.STAFF_NOT_FOUND]: {
    title: 'Staff Not Found',
    message: 'The staff member could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.STAFF_INVITATION_EXPIRED]: {
    title: 'Invitation Expired',
    message: 'This invitation has expired. Please request a new one.',
    action: 'Request new invitation',
    actionKey: 'retry',
  },
  [ErrorCode.STAFF_INVITATION_INVALID]: {
    title: 'Invalid Invitation',
    message: 'This invitation is invalid or has already been used.',
    action: 'Contact admin',
    actionKey: 'contact_support',
  },
  [ErrorCode.STAFF_ALREADY_EXISTS]: {
    title: 'Staff Exists',
    message: 'This person is already a staff member.',
    action: 'Go Back',
    actionKey: 'go_back',
  },

  // Inventory errors
  [ErrorCode.INVENTORY_NOT_FOUND]: {
    title: 'Item Not Found',
    message: 'This inventory item could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.INVENTORY_UNAVAILABLE]: {
    title: 'Item Unavailable',
    message: 'This item is currently unavailable.',
    action: 'Choose another',
    actionKey: 'choose_another',
  },
  [ErrorCode.INVENTORY_REQUEST_PENDING]: {
    title: 'Request Pending',
    message: 'A request for this item is already pending.',
    action: 'View requests',
    actionKey: 'go_back',
  },

  // Notification errors
  [ErrorCode.NOTIFICATION_NOT_FOUND]: {
    title: 'Notification Not Found',
    message: 'This notification could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },

  // Payout errors
  [ErrorCode.PAYOUT_NOT_FOUND]: {
    title: 'Payout Not Found',
    message: 'This payout could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.PAYOUT_INVALID_STATUS]: {
    title: 'Invalid Payout Status',
    message: 'This payout cannot be modified in its current status.',
    action: 'Review payout',
    actionKey: 'go_back',
  },
  [ErrorCode.PAYOUT_NO_ELIGIBLE_BOOKINGS]: {
    title: 'No Eligible Bookings',
    message: 'No bookings are available for payout in this period.',
    action: 'Try different period',
    actionKey: 'retry',
  },
  [ErrorCode.PAYOUT_BANK_ACCOUNT_NOT_FOUND]: {
    title: 'Bank Account Not Found',
    message: 'Please add your bank account details first.',
    action: 'Add bank account',
    actionKey: 'go_back',
  },
  [ErrorCode.PAYOUT_PROCESSING_FAILED]: {
    title: 'Payout Processing Failed',
    message: 'Failed to process payout. Please try again later.',
    action: 'Try Again',
    actionKey: 'retry',
  },
  [ErrorCode.PAYOUT_INVALID_AMOUNT]: {
    title: 'Invalid Payout Amount',
    message:
      'The calculated payout amount is invalid. Please review the booking amounts and deductions.',
    action: 'Review Payout',
    actionKey: 'go_back',
  },
  [ErrorCode.PAYOUT_INVALID_STATUS_TRANSITION]: {
    title: 'Invalid Status Transition',
    message: 'This payout cannot be transitioned to the requested status from its current state.',
    action: 'Review payout',
    actionKey: 'go_back',
  },

  // Upload errors
  [ErrorCode.UPLOAD_FILE_TOO_LARGE]: {
    title: 'File Too Large',
    message: 'The file you selected is too large.',
    action: 'Choose smaller file',
    actionKey: 'choose_another',
  },
  [ErrorCode.UPLOAD_INVALID_FORMAT]: {
    title: 'Invalid Format',
    message: 'The file format is not supported.',
    action: 'Choose valid file',
    actionKey: 'choose_another',
  },
  [ErrorCode.UPLOAD_FAILED]: {
    title: 'Upload Failed',
    message: 'The file could not be uploaded.',
    action: 'Retry',
    actionKey: 'retry',
  },

  // Validation errors
  [ErrorCode.VALIDATION_ERROR]: {
    title: 'Validation Error',
    message: 'Please check your input and try again.',
    action: 'Review form',
    actionKey: 'retry',
  },
  [ErrorCode.VALIDATION_REQUIRED_FIELD]: {
    title: 'Required Field',
    message: 'Please fill in all required fields.',
    action: 'Review form',
    actionKey: 'retry',
  },
  [ErrorCode.VALIDATION_INVALID_FORMAT]: {
    title: 'Invalid Format',
    message: 'Please enter data in the correct format.',
    action: 'Review form',
    actionKey: 'retry',
  },

  // Rate limiting
  [ErrorCode.RATE_LIMIT_EXCEEDED]: {
    title: 'Too Many Requests',
    message: 'Please wait a moment before trying again.',
    action: 'Wait and retry',
    actionKey: 'retry',
  },

  // Server errors
  [ErrorCode.SERVER_ERROR]: {
    title: 'Server Error',
    message: 'Something went wrong. Please try again later.',
    action: 'Try again later',
    actionKey: 'retry',
  },
  [ErrorCode.SERVICE_UNAVAILABLE]: {
    title: 'Service Unavailable',
    message: 'The service is temporarily unavailable.',
    action: 'Retry',
    actionKey: 'retry',
  },
  [ErrorCode.DATABASE_ERROR]: {
    title: 'Database Error',
    message: 'A database error occurred. Please try again.',
    action: 'Retry',
    actionKey: 'retry',
  },

  // Resource errors
  [ErrorCode.RESOURCE_NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.RESOURCE_CONFLICT]: {
    title: 'Conflict',
    message: 'This operation conflicts with existing data.',
    action: 'Try again',
    actionKey: 'retry',
  },
  [ErrorCode.RESOURCE_GONE]: {
    title: 'Resource Removed',
    message: 'This resource is no longer available.',
    action: 'Go Back',
    actionKey: 'go_back',
  },

  // Operation errors
  [ErrorCode.OPERATION_NOT_ALLOWED]: {
    title: 'Operation Not Allowed',
    message: 'This operation cannot be performed.',
    action: 'Go Back',
    actionKey: 'go_back',
  },

  // Config errors
  [ErrorCode.VENUE_TYPE_CONFIG_NOT_FOUND]: {
    title: 'Venue Type Not Found',
    message: 'The requested venue type configuration could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.VENUE_TYPE_CONFIG_CODE_EXISTS]: {
    title: 'Venue Type Exists',
    message: 'A venue type with this code already exists.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.AMENITY_CONFIG_NOT_FOUND]: {
    title: 'Amenity Not Found',
    message: 'The requested amenity configuration could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.AMENITY_CONFIG_CODE_EXISTS]: {
    title: 'Amenity Exists',
    message: 'An amenity with this code already exists.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.INVENTORY_CATEGORY_CONFIG_NOT_FOUND]: {
    title: 'Category Not Found',
    message: 'The requested inventory category could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.INVENTORY_CATEGORY_CONFIG_CODE_EXISTS]: {
    title: 'Category Exists',
    message: 'An inventory category with this code already exists.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.ALLERGEN_CONFIG_NOT_FOUND]: {
    title: 'Allergen Not Found',
    message: 'The requested allergen configuration could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.ALLERGEN_CONFIG_CODE_EXISTS]: {
    title: 'Allergen Exists',
    message: 'An allergen with this code already exists.',
    action: 'Try another code',
    actionKey: 'retry',
  },
  [ErrorCode.SPICE_LEVEL_CONFIG_NOT_FOUND]: {
    title: 'Spice Level Not Found',
    message: 'The requested spice level configuration could not be found.',
    action: 'Go Back',
    actionKey: 'go_back',
  },
  [ErrorCode.SPICE_LEVEL_CONFIG_LEVEL_EXISTS]: {
    title: 'Spice Level Exists',
    message: 'A spice level with this value already exists.',
    action: 'Try another level',
    actionKey: 'retry',
  },

  // Feature management
  [ErrorCode.FEATURE_NOT_AVAILABLE]: {
    title: 'Feature Not Available',
    message: 'This feature is not currently available.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },

  // Referral
  [ErrorCode.REFERRAL_NOT_CONFIGURED]: {
    title: 'Referral Not Configured',
    message: 'The referral system is not currently configured.',
    action: 'Contact Support',
    actionKey: 'contact_support',
  },

  // Addon Pricing
  [ErrorCode.ADDON_PRICING_NOT_CONFIGURED]: {
    title: 'Addon Pricing Not Configured',
    message: 'Pricing for this add-on has not been configured. Please contact the venue.',
    action: 'Contact Venue',
    actionKey: 'contact_support',
  },

  // Bundles
  [ErrorCode.BUNDLE_NOT_FOUND]: {
    title: 'Bundle Not Found',
    message: 'The requested bundle could not be found.',
    action: 'Browse Bundles',
    actionKey: 'retry',
  },
  [ErrorCode.BUNDLE_NO_DISCOUNT]: {
    title: 'No Bundle Discount',
    message: 'This bundle does not offer a discount over individual item prices.',
    action: 'Continue',
    actionKey: 'retry',
  },

  // Agent / PC unlock
  [ErrorCode.AGENT_SIGNATURE_INVALID]: {
    title: 'Invalid QR Code',
    message: 'The QR code signature is invalid. Please scan a fresh QR code.',
    action: 'Scan again',
    actionKey: 'retry',
  },
  [ErrorCode.AGENT_QR_EXPIRED]: {
    title: 'QR Code Expired',
    message: 'The QR code has expired. Please scan a fresh QR code from the lockscreen.',
    action: 'Scan again',
    actionKey: 'retry',
  },
  [ErrorCode.AGENT_UNIT_NOT_FOUND]: {
    title: 'PC Not Found',
    message: 'The selected PC could not be found or is not available.',
    action: 'Go back',
    actionKey: 'go_back',
  },
  [ErrorCode.AGENT_UNIT_NOT_IDLE]: {
    title: 'PC In Use',
    message: 'This PC is currently in use. Please select another PC.',
    action: 'Choose another',
    actionKey: 'choose_another',
  },
  [ErrorCode.AGENT_PAYMENT_NOT_FOUND]: {
    title: 'Payment Not Found',
    message: 'The payment could not be verified. Please try again.',
    action: 'Try again',
    actionKey: 'retry',
  },
};

/**
 * Technical message patterns to error codes mapping
 * Used for backwards compatibility with old endpoints
 */
export const TECHNICAL_MESSAGE_PATTERNS: Array<{
  pattern: RegExp;
  errorCode: ErrorCodeValue;
}> = [
  { pattern: /invalid credentials/i, errorCode: ErrorCode.AUTH_INVALID_CREDENTIALS },
  { pattern: /user not found/i, errorCode: ErrorCode.USER_NOT_FOUND },
  { pattern: /invalid email or password/i, errorCode: ErrorCode.AUTH_INVALID_CREDENTIALS },
  { pattern: /unauthorized/i, errorCode: ErrorCode.AUTH_UNAUTHORIZED },
  { pattern: /email not verified/i, errorCode: ErrorCode.AUTH_EMAIL_NOT_VERIFIED },
  { pattern: /account disabled/i, errorCode: ErrorCode.AUTH_ACCOUNT_DISABLED },
  { pattern: /too many attempts/i, errorCode: ErrorCode.AUTH_ACCOUNT_LOCKED },
  { pattern: /refreshtoken should not be empty/i, errorCode: ErrorCode.AUTH_SESSION_EXPIRED },
  { pattern: /slot.*unavailable/i, errorCode: ErrorCode.BOOKING_SLOT_UNAVAILABLE },
  { pattern: /already.*cancelled/i, errorCode: ErrorCode.BOOKING_ALREADY_CANCELLED },
  { pattern: /payment failed/i, errorCode: ErrorCode.PAYMENT_FAILED },
  { pattern: /coupon.*invalid/i, errorCode: ErrorCode.COUPON_INVALID },
  { pattern: /coupon.*expired/i, errorCode: ErrorCode.COUPON_EXPIRED },
  { pattern: /venue not found/i, errorCode: ErrorCode.VENUE_NOT_FOUND },
  { pattern: /booking not found/i, errorCode: ErrorCode.BOOKING_NOT_FOUND },
  { pattern: /file.*too large/i, errorCode: ErrorCode.UPLOAD_FILE_TOO_LARGE },
  { pattern: /invalid.*format/i, errorCode: ErrorCode.VALIDATION_INVALID_FORMAT },
  // Prisma unique constraint patterns (fallback if structured parsing fails)
  { pattern: /unique constraint.*email/i, errorCode: ErrorCode.USER_EMAIL_EXISTS },
  { pattern: /unique constraint.*phone/i, errorCode: ErrorCode.USER_PHONE_EXISTS },
];
